/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import {ThunkAction} from 'model/store/thunk'
import {Reminder} from 'types/entities/Reminder'
import {deleteReminder, pushReminder, setReminders, setRemindersTableMeta, setSelectedReminder,} from './RemindersReducer'
import RequestAction from 'store/RequestAction'
import {parsePaginatedResponse} from 'utils/api/pagination'
import { toApiReminderModelName } from 'utils/reminders/reminderUtils'
import { MassActionType } from 'types/enums/specific/MassAction'

export class RemindersAction {
    static fetchRemindersTablePage = (params: {
        page: number
        per_page: number
        search?: any
        sorting?: { id: string; desc: boolean }[]
        sort_by?: string | null
        sort?: 'asc' | 'desc' | null
    }): ThunkAction<
        Promise<{
            data: Reminder[]
            current_page: number
            per_page: number
            total: number
        }>
    > => {
        return async (
            dispatch,
            getState
        ): Promise<{
            data: Reminder[]
            current_page: number
            per_page: number
            total: number
        }> => {
            const url = `/admin/reminder/table`

            const response: any = await RequestAction.makeAuthenticatedRequest(
                getState,
                url,
                {
                    page: params.page,
                    per_page: params.per_page,
                    search: params.search,
                    sorting: params.sorting,
                },
                'post'
            )
            try {
                // If the response contains 'reminders' key, use it. Otherwise fallback to root structure.
                const key = response?.reminders ? 'reminders' : undefined
                const parsedResponse = parsePaginatedResponse<Reminder>(
                    response,
                    key
                )

                dispatch(setReminders(parsedResponse.data))
                dispatch(
                    setRemindersTableMeta({
                        currentPage: parsedResponse.current_page,
                        perPage: parsedResponse.per_page,
                        total: parsedResponse.total,
                        search: params.search,
                    })
                )

                return parsedResponse
            } catch (e) {
                console.error(
                    'Error parsing reminders table response',
                    e,
                    response
                )
                return Promise.reject(response)
            }
        }
    }

    static fetchExportRemindersXlsx = (
        params: any
    ): ThunkAction<Promise<Blob>> => {
        return async (_dispatch, getState): Promise<Blob> => {
			const url = `/admin/export/reminders`
            return await RequestAction.makeAuthenticatedRequest(
                getState,
                url,
                {
                    ...params,
                },
                'post',
                'blob'
            )
        }
    }

    static fetchMassAction = (
        payload: {
            ids: number[]
            action: MassActionType | string
            params?: Record<string, unknown>
        }
    ): ThunkAction<Promise<any>> => {
        return async (_dispatch, getState) => {
            try {
                return await RequestAction.makeAuthenticatedRequest(
                    getState,
                    '/admin/reminder/mass-actions',
                    payload,
                    'post'
                )
            } catch (error) {
                return Promise.reject(error)
            }
        }
    }

    static fetchCreateReminder = (
        reminderData: Partial<Reminder>
    ): ThunkAction<Promise<Reminder>> => {
        return async (dispatch, getState): Promise<Reminder> => {
            // Exception: opportunity → lead (backend has no Opportunity Eloquent class).
            const payload = {
                ...reminderData,
                ...(reminderData.model_name != null
                    ? { model_name: toApiReminderModelName(reminderData.model_name) ?? reminderData.model_name }
                    : {}),
            }

            const response: any = await RequestAction.makeAuthenticatedRequest(
                getState,
                '/admin/reminder/create',
                payload,
                'post'
            )

            const reminder: Reminder | undefined = response?.reminder

            if (reminder) {
                dispatch(pushReminder(reminder))
                return reminder
            }
            return Promise.reject(response)
        }
    }

    static fetchReminderDetail = (
        reminderId: number
    ): ThunkAction<Promise<Reminder>> => {
        return async (dispatch, getState): Promise<Reminder> => {
            const response: { reminder: Reminder } =
                await RequestAction.makeAuthenticatedRequest(
                    getState,
                    `/admin/reminder/detail/${reminderId}`,
                    undefined,
                    'get'
                )

            if (response?.reminder) {
                dispatch(setSelectedReminder(response.reminder))
                return response.reminder
            }
            return Promise.reject(response)
        }
    }

    static fetchDeleteReminder = (
        reminderId: number
    ): ThunkAction<Promise<void>> => {
        return async (dispatch, getState): Promise<void> => {
            const response: { success: boolean } =
                await RequestAction.makeAuthenticatedRequest(
                    getState,
                    `/admin/reminder/delete/${reminderId}`,
                    undefined,
                    'delete'
                )

            if (response?.success) {
                dispatch(deleteReminder(reminderId))
                return Promise.resolve()
            }
            return Promise.reject(response)
        }
    }

    static setSelectedReminder = (reminder: Reminder | null) => {
        return setSelectedReminder(reminder)
    }
}
